import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { VirtualVehicleVehicle } from '@/@types/VirtualVehicle';
import useBranch from '../../../../data/branch/useBranch';
import usePermissionProcessFlow from '../../../../data/permissions/usePermissionProcessFlow';
import useActivePurchaseProcesses from '../../../../data/pf/useActivePurchaseProcesses';
import type { DetailedVehicle } from '../../../../data/vehicles/useDetailedVehicle';
import usePurchaseInfoTypes from '../../../../data/vehicles/usePurchaseInfoTypes';
import useVehicleExplore, {
  type VehicleExplore,
} from '../../../../data/vehicles/useVehicleExplore';
import AddButton from '../../../../styled-components/components/AddButton';
import { Box } from '../../../../styled-components/components/Box';
import BulletSeparatedList from '../../../../styled-components/components/BulletSeparatedList';
import { Button } from '../../../../styled-components/components/Button';
import { Column, Columns } from '../../../../styled-components/components/Columns';
import { Form, FormGroup } from '../../../../styled-components/components/Form';
import { H5 } from '../../../../styled-components/components/Heading';
import InfoBox from '../../../../styled-components/components/InfoBox';
import InputCheckbox from '../../../../styled-components/components/InputCheckbox';
import InputSelect from '../../../../styled-components/components/InputSelect';
import InputText from '../../../../styled-components/components/InputText';
import SegmentedControl from '../../../../styled-components/components/SegmentedControl';
import { StackLarge, StackSmall } from '../../../../styled-components/components/Stack';
import { Text } from '../../../../styled-components/components/Text';
import { VStack } from '../../../../styled-components/components/VStack';
import { formatYYYYMMDDLocale, useDateLocale } from '../../../../utils/date';
import { getMarketCode } from '../../../../utils/market';
import { purchaseInfoTypesLabel } from '../../Item/Shared/StatusInformationAside/PurchaseInfoType/PurchaseInfoHelpers';
import AddFromTemplate from './AddFromTemplate';
import ExistingVehicles from './ExistingVehicles';
import { RegVinInput } from './RegVinInput';
import type { CreateVehiclePanels } from './utils';
import {
  applyProcess,
  odometerReadingUnit,
  odometerReadingValue,
  purchaseType,
  registrationOrVinNumber,
  virtualVehicleId,
} from './validationMethods';

const vehicleFormSchema = z.object({
  registrationOrVinNumber,
  virtualVehicleId: virtualVehicleId.optional(),
  odometerReadingValue,
  odometerReadingUnit,
  purchaseType,
  applyProcess,
  lookup: z.boolean().optional(),
  confirmDuplicate: z.boolean().optional(),
  modelYear: z
    .object({
      year: z.number().min(1900, 'modelYearLowerLimit').max(2050, 'modelYearUpperLimit').optional(),
      marketCode: z.enum(['SE', 'NO', 'US']),
    })
    .refine((data) => data.marketCode !== 'NO' || !!data.year, {
      message: 'modelYearValidationErrorMessage',
      path: ['year'],
    }),
});

export type VehicleFormSchema = z.infer<typeof vehicleFormSchema>;

export interface VehicleFormSubmitData {
  detailedVehicle?: DetailedVehicle;
  virtualVehicle?: VirtualVehicleVehicle;
  formData: VehicleFormSchema;
}

interface CreateRegisteredVehicleFormProps {
  formId: string;
  onSubmit: (data: VehicleFormSubmitData) => void;
  variant?: 'standalone' | 'embedded';
  context?: 'order';
  navigatePanel: (panel: CreateVehiclePanels) => void;
  showTemplateSelect: boolean;
  setShowTemplateSelect: (showTemplateSelect: boolean) => void;
  disableVirtual?: boolean;
  disabled?: boolean;
  onSelectExistingVehicle?: (vehicle: VehicleExplore) => void;
}

const CreateRegisteredVehicleForm = ({
  formId,
  onSubmit,
  variant = 'standalone',
  context,
  navigatePanel,
  showTemplateSelect,
  setShowTemplateSelect,
  disableVirtual,
  disabled,
  onSelectExistingVehicle,
}: CreateRegisteredVehicleFormProps) => {
  const { t } = useTranslation(['common', 'createVehiclePanel']);
  const [detailedVehicle, setDetailedVehicle] = useState<DetailedVehicle>();
  const [virtualVehicle, setVirtualVehicle] = useState<VirtualVehicleVehicle>();
  const [failedLookup, setFailedLookup] = useState(false);
  const [vehiclesModalType, setVehiclesModalType] = useState<'active' | 'archived' | null>(null);
  const [hasAutoOpenedActiveModal, setHasAutoOpenedActiveModal] = useState(false);
  const [processFlowPermission] = usePermissionProcessFlow();
  const [branch] = useBranch();
  const notAuthorized = !branch?.resellerFor.length;
  const [processes] = useActivePurchaseProcesses();
  const [purchaseInfoTypes] = usePurchaseInfoTypes();
  const marketCode = getMarketCode();
  const locale = useDateLocale();

  // Fetch active and archived vehicles separately
  const [activeVehiclesFromAPI] = useVehicleExplore(
    detailedVehicle ? { id: detailedVehicle.registrationNumber, archived: false } : undefined,
  );
  const [archivedVehiclesFromAPI] = useVehicleExplore(
    detailedVehicle ? { id: detailedVehicle.registrationNumber, archived: true } : undefined,
  );

  const {
    register,
    formState: { errors, dirtyFields },
    trigger,
    control,
    handleSubmit,
    setValue,
    setError,
  } = useForm<VehicleFormSchema>({
    mode: 'onSubmit',
    resolver: zodResolver(vehicleFormSchema),
    defaultValues: {
      lookup: false,
      confirmDuplicate: false,
      registrationOrVinNumber: { marketCode, registrationNumber: '' },
      odometerReadingUnit: marketCode === 'SE' ? 'mil' : 'km',
      modelYear: { marketCode },
    },
  });

  useEffect(() => {
    if (detailedVehicle && marketCode === 'NO' && !dirtyFields.modelYear?.year) {
      setValue('modelYear.year', detailedVehicle.modelYear);
    }
  }, [detailedVehicle, marketCode, dirtyFields.modelYear?.year, setValue]);

  const activeVehicles = activeVehiclesFromAPI ?? [];
  const archivedVehicles = archivedVehiclesFromAPI ?? [];

  const mostRecentArchivedDate =
    archivedVehicles.length > 0
      ? archivedVehicles
          .map((v) => v.archivedAt)
          .filter((date): date is string => !!date)
          .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0]
      : undefined;

  useEffect(() => {
    if (activeVehicles.length > 0 && !hasAutoOpenedActiveModal && vehiclesModalType === null) {
      setVehiclesModalType('active');
      setHasAutoOpenedActiveModal(true);
    }
  }, [activeVehicles.length, hasAutoOpenedActiveModal, vehiclesModalType]);

  const onFormSubmit = handleSubmit((data) => {
    if (activeVehicles.length > 0 && !data.confirmDuplicate) {
      setError('confirmDuplicate', {
        type: 'manual',
        message: 'confirmDuplicateRequired',
      });
      return;
    }
    onSubmit({
      detailedVehicle,
      virtualVehicle,
      formData: data,
    });
  });

  if (showTemplateSelect) {
    return (
      <AddFromTemplate
        onSelect={(virtualVehicle) => {
          setValue('virtualVehicleId', virtualVehicle.id);
          setVirtualVehicle(virtualVehicle);
        }}
        onClose={() => setShowTemplateSelect(false)}
      />
    );
  }

  const archivedOnThisBranch = archivedVehicles.some((v) => v.branchId === branch?.id);

  return (
    <>
      {vehiclesModalType === 'active' && activeVehicles.length > 0 && (
        <ExistingVehicles
          context={context}
          existingVehicles={activeVehicles}
          onClose={() => setVehiclesModalType(null)}
          onSelectVehicle={onSelectExistingVehicle}
        />
      )}
      {vehiclesModalType === 'archived' && archivedVehicles.length > 0 && (
        <ExistingVehicles
          existingVehicles={archivedVehicles}
          onClose={() => setVehiclesModalType(null)}
          showOnlyArchived
        />
      )}
      <form id={formId} onSubmit={onFormSubmit}>
        <StackLarge>
          <H5 as="h3" $margin="small">
            {t('vehicleInfoHeading', { ns: 'createVehiclePanel' })}
          </H5>
          <Form>
            {!disableVirtual && variant === 'standalone' && (
              <FormGroup>
                <SegmentedControl
                  label=""
                  options={[
                    {
                      label: t('withRegVin', { ns: 'createVehiclePanel' }),
                      title: t('withRegVin', { ns: 'createVehiclePanel' }),
                      onClick: () => null,
                    },
                    {
                      label: t('withoutRegVin', { ns: 'createVehiclePanel' }),
                      title: t('withoutRegVin', { ns: 'createVehiclePanel' }),
                      onClick: () => navigatePanel('OEM'),
                    },
                  ]}
                />
              </FormGroup>
            )}
            <FormGroup>
              <RegVinInput
                setValue={(vehicle) => setDetailedVehicle(vehicle)}
                control={control}
                names={{
                  hasDetails: 'lookup',
                  vehicleRegistration: 'registrationOrVinNumber.registrationNumber',
                }}
                onValidate={() => trigger('lookup')}
                onLookupFailure={(error) => setFailedLookup(error)}
                lookupFailureState={{
                  type: disableVirtual ? 'error' : 'warning',
                  message: t(disableVirtual ? 'vehicleLookupError' : 'vehicleLookupWarning', {
                    ns: 'createVehiclePanel',
                  }),
                }}
              />
            </FormGroup>
            {failedLookup && !disableVirtual && (
              <FormGroup>
                {!virtualVehicle ? (
                  <StackSmall>
                    <AddButton
                      onClick={() => setShowTemplateSelect(true)}
                      title={t('selectTemplate', { ns: 'createVehiclePanel' })}
                      disabled={notAuthorized}
                      inputHeight
                    >
                      {t('selectTemplate', { ns: 'createVehiclePanel' })}
                    </AddButton>
                  </StackSmall>
                ) : virtualVehicle ? (
                  <Box>
                    <Columns $spacing={2} $valign="center">
                      <Column $grow>
                        <VStack $spacing={0.5}>
                          <Text $size="small">
                            <Text as="span" $weight="bold">
                              {virtualVehicle.manufacturer}
                            </Text>{' '}
                            {virtualVehicle.salesName}
                          </Text>
                          <BulletSeparatedList
                            size="small"
                            tone="alt"
                            items={[
                              virtualVehicle.modelYear,
                              virtualVehicle.drivingWheel,
                              virtualVehicle.batteryCapacity
                                ? `${virtualVehicle.batteryCapacity}kWh`
                                : undefined,
                            ]}
                          />
                        </VStack>
                      </Column>
                      <Column $noShrink>
                        <Button
                          $tone="secondary"
                          $size="small"
                          onClick={() => setShowTemplateSelect(true)}
                          title={t('changeButton', { ns: 'createVehiclePanel' })}
                        >
                          {t('changeButton', { ns: 'createVehiclePanel' })}
                        </Button>
                      </Column>
                    </Columns>
                  </Box>
                ) : null}
                {notAuthorized && (
                  <StackSmall>
                    <InfoBox>{t('unauthorizedRetailer', { ns: 'createVehiclePanel' })}</InfoBox>
                  </StackSmall>
                )}
              </FormGroup>
            )}
            <FormGroup>
              <InputText
                label={t('odometerLabel', { ns: 'createVehiclePanel' })}
                type="number"
                register={register('odometerReadingValue', { valueAsNumber: true })}
                error={t(errors.odometerReadingValue?.message ?? '', { ns: 'createVehiclePanel' })}
                unitSelect={{
                  title: t('unit', { ns: 'glossary' }) || '',
                  id: 'wip-odometerReadingUnit',
                  register: register('odometerReadingUnit', { required: true }),
                  disabled: disabled,
                  name: 'odometerReadingUnit',
                  options: [
                    { displayName: 'km', value: 'km' },
                    { displayName: 'mil', value: 'mil' },
                  ],
                }}
              />
            </FormGroup>
            {marketCode === 'NO' && !failedLookup && (
              <FormGroup>
                <InputText
                  label={t('modelYearLabel', {
                    ns: 'createVehiclePanel',
                  })}
                  register={register('modelYear.year', { valueAsNumber: true })}
                  name="modelYear"
                  error={t(errors.modelYear?.message ?? '', { ns: 'createVehiclePanel' })}
                  state={
                    detailedVehicle && !dirtyFields.modelYear?.year
                      ? {
                          type: 'info',
                          message: t('editableEnrichedData', { ns: 'createVehiclePanel' }),
                        }
                      : undefined
                  }
                />
              </FormGroup>
            )}
            {activeVehicles.length > 0 && (
              <FormGroup>
                <InputCheckbox
                  id="confirmDuplicate"
                  register={register('confirmDuplicate')}
                  error={t(errors.confirmDuplicate?.message ?? '', { ns: 'createVehiclePanel' })}
                >
                  {t('confirmDuplicateLabel', { ns: 'createVehiclePanel' })}{' '}
                  <button
                    type="button"
                    onClick={() => setVehiclesModalType('active')}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'inherit',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      padding: 0,
                      font: 'inherit',
                    }}
                  >
                    {t('showActiveVehicles', { ns: 'createVehiclePanel' })}
                  </button>
                </InputCheckbox>
              </FormGroup>
            )}
          </Form>
        </StackLarge>
        <StackLarge>
          <H5 as="h3" $margin="small">
            {t('moreInfoHeading', { ns: 'createVehiclePanel' })}
          </H5>
          <Form>
            <FormGroup>
              <InputSelect
                label={t('purchaseTypeLabel', { ns: 'createVehiclePanel' })}
                title={t('purchaseTypeTitle', { ns: 'createVehiclePanel' })}
                options={[
                  {
                    value: '',
                    displayName: t('purchaseTypeUnset', { ns: 'createVehiclePanel' }),
                    disabled: true,
                  },
                  ...(purchaseInfoTypes?.map((type) => ({
                    value: type.ofType,
                    displayName: purchaseInfoTypesLabel(type.ofType),
                  })) ?? []),
                ]}
                defaultValue=""
                help={t('purchaseTypeHelp', { ns: 'createVehiclePanel' })}
                register={register('purchaseType')}
                error={t(errors.purchaseType?.message ?? '')}
              />
            </FormGroup>
            {processFlowPermission?.enabled && (
              <FormGroup>
                <InputSelect
                  label={t('applyProcessLabel', { ns: 'createVehiclePanel' })}
                  title={t('applyProcessTitle', { ns: 'createVehiclePanel' })}
                  options={[
                    {
                      displayName: t('applyProcessUnset', { ns: 'createVehiclePanel' }),
                      value: '',
                      disabled: true,
                    },
                    ...(processes?.map((process) => ({
                      value: process.id,
                      displayName: process.displayName,
                    })) ?? []),
                  ]}
                  defaultValue=""
                  help={t('applyProcessHelp', { ns: 'createVehiclePanel' })}
                  register={register('applyProcess')}
                  error={t(errors.applyProcess?.message ?? '')}
                />
              </FormGroup>
            )}
            {archivedVehicles.length > 0 && (
              <FormGroup style={{ marginTop: '24px' }}>
                <InfoBox>
                  {archivedOnThisBranch
                    ? t('archivedVehiclesInfoSameBranch', {
                        ns: 'createVehiclePanel',
                        count: archivedVehicles.length,
                        date: formatYYYYMMDDLocale(mostRecentArchivedDate, locale),
                      })
                    : t('archivedVehiclesInfo', {
                        ns: 'createVehiclePanel',
                        count: archivedVehicles.length,
                        date: formatYYYYMMDDLocale(mostRecentArchivedDate, locale),
                      })}{' '}
                  <button
                    type="button"
                    onClick={() => setVehiclesModalType('archived')}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'inherit',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      padding: 0,
                      font: 'inherit',
                    }}
                  >
                    {t('showArchivedVehicles', { ns: 'createVehiclePanel' })}
                  </button>
                </InfoBox>
              </FormGroup>
            )}
          </Form>
        </StackLarge>
      </form>
    </>
  );
};

export default CreateRegisteredVehicleForm;
